import { createStore } from './store';
import type { Plan, PlanStop } from './actions';
import { newShare, type Share } from './sharing';
import type { GroupBy } from '../data/groups';
import { track } from '../data/analytics';

/**
 * Where the owner is in the control room and what's showing: the design page's `S` (control-room-web.html, "State").
 * Kept in memory; the theme, the setup card's "Hide" and how Stops is grouped are remembered in this browser only.
 */
export type Page = 'live' | 'routes' | 'couriers' | 'stops' | 'reports';
export type View = 'day' | 'route' | 'stop' | 'person' | 'plan' | 'closest' | 'share';
export type Rec = { type: 'stop' | 'courier' | 'waiting'; id: string } | null;

export interface UI {
  page: Page;
  view: View;
  /** The route open on Live. */
  route: string | null;
  /** The stop open on Live: its key (`routeId/stopId`). */
  stop: string | null;
  person: string | null;
  /** Where a stop's record goes back to. */
  back: 'day' | 'route';
  /** Live with the map beside the list, or the list alone as tables. */
  map: boolean;
  /** Routes hidden on the map. */
  hidden: Set<string>;
  theme: 'light' | 'dark';
  leftOnly: boolean;
  /** Stops: the tab, the search, one courier, what's selected, the sort, the columns hidden. */
  tab: string;
  q: string;
  courier: string | null;
  sel: Set<string>;
  sort: { k: string; dir: 1 | -1 } | null;
  hideCols: Set<string>;
  /** Stops, on the tabs other than Waiting: grouped by route or courier, or not (board `routes-web`). */
  group: GroupBy;
  /** Couriers: the tab and the search. */
  ctab: string;
  cq: string;
  /** The record docked beside a table. */
  rec: Rec;
  routeQ: string;
  /** Routes: "Find a stop in any route", and the route under the pointer, which the map keeps while the others fade. */
  routesQ: string;
  hover: string | null;
  /** Routes: a past day picked from the last 60 days already read; null for today. */
  rday: string | null;
  /** Reports: the days shown, and each courier apart. */
  range: 7 | 14 | 30;
  split: boolean;
  /** The "Finish setting up" card. */
  setup: boolean;
  /** Asks the map to frame what's showing again. */
  frame: number;
  /** Live's list: the sections folded away. */
  closed: Set<string>;
  /** A new route being planned on Live. */
  plan: Plan | null;
  /** Stops waiting for a route being shared out across couriers on Live (src/app/sharing.ts). */
  share: Share | null;
  /** Who's closest to a spot on the map. */
  closest: { at: { lat: number; lng: number }; label: string } | null;
  /** The line in Needs a look being cleared with a note. */
  noting: string | null;
  /** Stops: the filters added (money, pickups, late, no photo). */
  flt: Set<string>;
  /** Stops waiting for a route: the sort, and the filters for the day (`none`, `today`, a day) and where they came from (`typed`, `sheet:<file>`). */
  wsort: { k: string; dir: 1 | -1 } | null;
  wday: string | null;
  wsrc: string | null;
  /** Importing a sheet, on the waiting list's tab. */
  importing: boolean;
  /** The notifications panel, open under the bell. */
  notes: boolean;
  /** Couriers → Zones: the zone picked, one being drawn (its name, couriers and corners so far), its shape being changed. */
  zone: string | null;
  drawing: { name: string; couriers: string[]; path: { lat: number; lng: number }[] } | null;
  reshaping: boolean;
  /** The picked zone's name field takes the keys (Rename, from a menu or ⌘K). */
  renaming: boolean;
}

const remembered = (() => {
  try {
    return JSON.parse(localStorage.getItem('routr-control-room') ?? '{}') as { theme?: string; setup?: boolean; group?: string };
  } catch {
    return {};
  }
})();

const initial = (): UI => ({
  page: 'live',
  view: 'day',
  route: null,
  stop: null,
  person: null,
  back: 'route',
  map: true,
  hidden: new Set(),
  theme: remembered.theme === 'dark' ? 'dark' : 'light',
  leftOnly: false,
  tab: 'all',
  q: '',
  courier: null,
  sel: new Set(),
  sort: null,
  hideCols: new Set(),
  group: remembered.group === 'route' || remembered.group === 'courier' ? remembered.group : 'none',
  ctab: 'all',
  cq: '',
  rec: null,
  routeQ: '',
  routesQ: '',
  hover: null,
  rday: null,
  range: 14,
  split: false,
  setup: remembered.setup !== false,
  frame: 0,
  closed: new Set(['watch']),
  plan: null,
  share: null,
  closest: null,
  noting: null,
  flt: new Set(),
  wsort: null,
  wday: null,
  wsrc: null,
  importing: false,
  notes: false,
  zone: null,
  drawing: null,
  reshaping: false,
  renaming: false,
});

export const ui = createStore<UI>(initial());

/** Leaving (signing out): nothing of one business's stays on screen for the next account; the theme stays. */
export const resetUi = () => ui.set((s) => ({ ...initial(), theme: s.theme, setup: s.setup, map: s.map, group: s.group }));

function keep() {
  const s = ui.get();
  try {
    localStorage.setItem('routr-control-room', JSON.stringify({ theme: s.theme, setup: s.setup, group: s.group }));
  } catch {
    // Private windows can refuse; the page works the same without it.
  }
}

const reframe = () => ui.set((s) => ({ frame: s.frame + 1 }));

/** Live and Routes: the list beside the map, sharing what's open in it. */
export const onMapPage = (p: Page) => p === 'live' || p === 'routes';
/** The list beside the map is showing: Live with its map on, or Routes, which always has it. */
export const besideMap = (s: Pick<UI, 'page' | 'map'>) => (s.page === 'live' && s.map) || s.page === 'routes';
/** A route or a courier opened from Routes stays on Routes, so Back goes back to its list. */
const mapPage = (): Page => (ui.get().page === 'routes' ? 'routes' : 'live');

export function go(page: Page) {
  const s = ui.get();
  // Live and Routes each open on their own list.
  const fresh = page !== s.page && (page === 'routes' || s.page === 'routes');
  ui.set({ page, sel: new Set(), rec: null, hover: null, ...(fresh ? { view: 'day' as View, route: null, stop: null, person: null, closest: null, rday: null } : {}) });
  if (onMapPage(page)) reframe();
}

export function openRoute(id: string) {
  ui.set((s) => ({ page: mapPage(), view: 'route', route: id, stop: null, person: null, hover: null, routeQ: '', leftOnly: false, rec: null, ...(s.route !== id ? { sel: new Set<string>() } : {}) }));
  reframe();
}

/** A courier on Live: their route running, else the one waiting, else today's last; else their own view. */
export function openPerson(uid: string, routeOf: (uid: string) => string | null) {
  const r = routeOf(uid);
  if (r) return openRoute(r);
  ui.set({ page: mapPage(), view: 'person', person: uid, route: null, stop: null, sel: new Set() });
  reframe();
}

/** A stop: in the list beside the map on Live, else its record docked beside the table. */
export function openStop(key: string, from?: 'day' | 'route') {
  const s = ui.get();
  if (!besideMap(s)) {
    ui.set({ rec: { type: 'stop', id: key } });
    return;
  }
  const routeId = key.split('/')[0];
  const moved = s.route !== routeId;
  ui.set({
    view: 'stop',
    stop: key,
    route: routeId,
    person: null,
    back: from ?? (s.view === 'day' ? 'day' : s.view === 'stop' ? s.back : 'route'),
  });
  if (moved) reframe();
}

export function openCourier(uid: string) {
  const s = ui.get();
  ui.set({ page: onMapPage(s.page) ? 'couriers' : s.page, rec: { type: 'courier', id: uid } });
}

export const closeRec = () => ui.set({ rec: null });

export function backToDay() {
  // A route being planned is kept (the day shows it); what was ticked on a route's table isn't.
  ui.set({ view: 'day', route: null, stop: null, person: null, closest: null, noting: null, hover: null, sel: new Set() });
  reframe();
}

export function backFromStop() {
  if (ui.get().back === 'day') return backToDay();
  ui.set({ view: 'route', stop: null });
}

export function toggleSec(id: string) {
  ui.set((s) => {
    const closed = new Set(s.closed);
    if (closed.has(id)) closed.delete(id);
    else closed.add(id);
    return { closed };
  });
}

export function setMap(on: boolean) {
  const s = ui.get();
  if (on === s.map) return;
  if (!on && s.view === 'stop' && s.stop) ui.set({ map: false, rec: { type: 'stop', id: s.stop }, view: 'route' });
  else if (on && s.page === 'live' && s.rec?.type === 'stop') {
    const key = s.rec.id;
    ui.set({ map: true, rec: null, view: 'stop', stop: key, route: key.split('/')[0] });
  } else ui.set({ map: on, rec: on ? null : s.rec });
  if (on) reframe();
}

export function hide(routeId: string) {
  ui.set((s) => {
    const hidden = new Set(s.hidden);
    if (hidden.has(routeId)) hidden.delete(routeId);
    else hidden.add(routeId);
    return { hidden };
  });
}

export function theme() {
  ui.set((s) => ({ theme: s.theme === 'dark' ? 'light' : 'dark' }));
  keep();
}

/** Stops grouped by route, by courier, or not: kept for this computer. */
export function setGroup(by: GroupBy) {
  if (ui.get().group === by) return;
  ui.set({ group: by });
  keep();
  track('live_action', { action: 'stops_grouped', by });
}

export function hideSetup() {
  ui.set({ setup: false });
  keep();
}

export const frame = reframe;

/** A new route on Live: from nothing, from a stop, or for one courier. */
/**
 * Plan a route: carries on with the one being planned, adding [stops] (each once) and who takes it when given; a fresh
 * one only when none is being planned. Leaving keeps it: Live's day says so, to carry on or discard.
 */
export function planWith(stops: PlanStop[] = [], to: string | null = null) {
  const cur = ui.get().plan;
  const keep = cur && cur.stops.length ? cur : null;
  // Two waiting stops can share an address (two orders to one office): each is its own.
  const keyOf = (x: PlanStop) => x.waitingId ?? x.fromKey ?? x.address;
  const have = new Set(keep?.stops.map(keyOf) ?? []);
  const plan = keep ? { ...keep, stops: [...keep.stops, ...stops.filter((x) => !have.has(keyOf(x)))], to: to ?? keep.to } : { stops, to, name: '' };
  ui.set({ page: 'live', map: true, view: 'plan', plan, route: null, stop: null, person: null, rec: null, closest: null, sel: new Set() });
  reframe();
}

/** The route being planned, put away; Undo brings it back. */
export function discardPlan(say: (msg: string, undo: () => void) => void) {
  const s = ui.get();
  const plan = s.plan;
  if (!plan) return;
  ui.set({ plan: null, view: s.view === 'plan' ? 'day' : s.view });
  say(`New route discarded · ${plan.stops.length === 1 ? '1 stop' : `${plan.stops.length} stops`}`, () => ui.set({ plan, view: 'plan', page: 'live', map: true }));
}

/** The Stops page's list of stops waiting for a route, with one stop's record open when given. */
export function openWaiting(id?: string) {
  ui.set({ page: 'stops', tab: 'wait', sel: new Set(), rec: id ? { type: 'waiting', id } : null });
}

/** Import a sheet: on the Stops page, beside the waiting list it fills. */
export const importSheet = () => ui.set({ page: 'stops', tab: 'wait', importing: true, sel: new Set(), rec: null });

/** Who's closest to a spot (closest_sheet.dart), in the list beside the map. */
export function closestTo(at: { lat: number; lng: number }, label: string) {
  ui.set({ page: 'live', map: true, view: 'closest', closest: { at, label }, route: null, stop: null, person: null, rec: null });
  reframe();
}

/** Couriers → Zones, with one zone picked when given. */
export function openZones(id: string | null = null) {
  ui.set((s) => ({ page: 'couriers', ctab: 'zones', zone: id, rec: null, reshaping: false, renaming: false, sel: new Set<string>(), drawing: id ? null : s.drawing }));
}

/** Draw a new zone on the zones map: clicks add corners. [couriers]: who it's for, when it's drawn from a courier. */
export function drawZone(couriers: string[] = []) {
  ui.set({ page: 'couriers', ctab: 'zones', zone: null, rec: null, reshaping: false, renaming: false, drawing: { name: '', couriers, path: [] } });
}

/**
 * Share out waiting stops across couriers, on Live beside the map: [ids], with every courier picked to start with (or the
 * share already open, with these stops added).
 */
export function openShare(ids: string[], couriers: string[]) {
  const cur = ui.get().share;
  const share = cur ? { ...cur, ids: [...new Set([...cur.ids, ...ids])] } : newShare(ids, couriers);
  ui.set({ page: 'live', map: true, view: 'share', share, route: null, stop: null, person: null, rec: null, closest: null, sel: new Set() });
  reframe();
}

/** The share, put away; Undo brings it back. */
export function discardShare(say: (msg: string, undo: () => void) => void) {
  const s = ui.get();
  const share = s.share;
  if (!share) return;
  ui.set({ share: null, view: s.view === 'share' ? 'day' : s.view, sel: new Set() });
  say(`Sharing out put away · ${share.ids.length === 1 ? '1 stop' : `${share.ids.length} stops`}`, () => ui.set({ share, view: 'share', page: 'live', map: true }));
}

/** A change to the share being worked on. */
export const setShare = (f: (x: Share) => Partial<Share>) => ui.set((s) => (s.share ? { share: { ...s.share, ...f(s.share) } } : {}));

